import React from 'react';
import DatePicker from 'react-datepicker';
import { DateInputLayoutProps } from 'ui-builder';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from 'shadcn-ui';
import ShadcnBaseInputLayout from '../../../form/layout/ShadcnBaseInputLayout';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './ShadcnDatePicker.module.scss';

const ShadcnDateTrigger = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ value, onClick, placeholder, disabled }, ref) => (
  <div className="relative">
    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none" />
    <Input
      ref={ref}
      value={(value as string) || ''}
      onClick={onClick}
      placeholder={placeholder}
      disabled={disabled}
      readOnly
      className="!pl-10 cursor-pointer"
    />
  </div>
));
ShadcnDateTrigger.displayName = 'ShadcnDateTrigger';

function ShadcnDateInputLayout(props: DateInputLayoutProps) {
  let value;

  if (props.value) {
    if (typeof props.value === 'string') {
      value = new Date(props.value);
    } else {
      value = props.value as Date;
    }
  } else {
    value = null;
  }

  return (
    <ShadcnBaseInputLayout
      label={props.label}
      afterLabelElement={props.afterLabelElement}
      name={props.name}
      errors={props.errors}
      hideLabel={props.hideLabel}
      showRequiredIcon={props.showRequiredIcon}
    >
      <div className={styles.shadcnDatePicker}>
        <DatePicker
          selected={value}
          placeholderText={props.placeholder}
          disabled={props.isDisabled}
          minDate={props.minDate}
          onChange={(date: Date | null) => {
            props.onChangeCallback({
              target: {
                value: date,
              },
            });
          }}
          customInput={<ShadcnDateTrigger />}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="flex items-center justify-between px-[8px] py-[8px]">
              <button
                type="button"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                aria-label="Previous month"
                className="p-[4px] rounded-full hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft className="size-[16px]" />
              </button>
              <span className="text-[14px] font-medium text-gray-700">
                {date.toLocaleString('default', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
              <button
                type="button"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                aria-label="Next month"
                className="p-[4px] rounded-full hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight className="size-[16px]" />
              </button>
            </div>
          )}
        />
      </div>
    </ShadcnBaseInputLayout>
  );
}

export default ShadcnDateInputLayout;
